import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../api/apiConfig";
import { fetchWithAuth } from "../api/fetchWithAuth";

const employeeId = employee => employee?.id
  ?? employee?.empId
  ?? employee?.employeeId?.id
  ?? employee?.employeeId
  ?? employee?.employee?.id
  ?? "";

const employeeCode = employee => employee?.empCode
  ?? employee?.empcode
  ?? employee?.employeeCode
  ?? employee?.employee?.empCode
  ?? "";

const employeeName = employee => employee?.name
  ?? employee?.fullName
  ?? employee?.employeeName
  ?? employee?.empName
  ?? employee?.employee?.name
  ?? "";

const employeeOptionValue = employee => String(employeeId(employee) || employeeCode(employee) || employeeName(employee));
const employeeDisplayName = employee => employeeName(employee) || employeeCode(employee);
const employeeLabel = employee => {
  const name = employeeDisplayName(employee);
  const code = employeeCode(employee);
  return code && name && code !== name ? `${code} - ${name}` : name;
};

const getEmployeeRows = result => {
  if (Array.isArray(result)) return result;
  if (result && typeof result === "object") {
    return ["data", "rows", "records", "items", "content", "result", "results", "list"]
      .map(key => result[key])
      .find(Array.isArray) || null;
  }
  return null;
};

const getSelectedEmployeeValue = (value, employees) => {
  if (value && typeof value === "object") {
    const employee = employees.find(item => {
      const id = employeeId(value);
      const code = employeeCode(value);
      return (id && String(employeeId(item)) === String(id))
        || (code && String(employeeCode(item)).toLowerCase() === String(code).toLowerCase())
        || (employeeName(value) && employeeName(item) === employeeName(value));
    });
    return employee ? employeeOptionValue(employee) : employeeOptionValue(value);
  }

  const selectedValue = String(value ?? "");
  const employee = employees.find(item =>
    [employeeOptionValue(item), employeeCode(item), employeeName(item)]
      .some(candidate => candidate && String(candidate).toLowerCase() === selectedValue.toLowerCase())
  );
  return employee ? employeeOptionValue(employee) : selectedValue;
};

const getSelectedEmployee = (value, employees) => {
  if (value && typeof value === "object") {
    const id = employeeId(value);
    const code = employeeCode(value);
    return employees.find(employee =>
      (id && String(employeeId(employee)) === String(id))
      || (code && String(employeeCode(employee)).toLowerCase() === String(code).toLowerCase())
    ) || value;
  }

  const selectedValue = String(value ?? "");
  return employees.find(employee =>
    [employeeOptionValue(employee), employeeCode(employee), employeeName(employee)]
      .some(candidate => candidate && String(candidate).toLowerCase() === selectedValue.toLowerCase())
  ) || null;
};

function FuelPumpForm({ selectedSite, onSave, onClose, initialData = null }) {
  const formInitialData = initialData ?? null;
  const [documentFile, setDocumentFile] = useState(null);
  const [receivedByEmployees, setReceivedByEmployees] = useState([]);
  const [approvedByEmployees, setApprovedByEmployees] = useState([]);
  const [receivedByLoading, setReceivedByLoading] = useState(true);
  const [approvedByLoading, setApprovedByLoading] = useState(true);
  const [receivedByError, setReceivedByError] = useState("");
  const [approvedByError, setApprovedByError] = useState("");
  const [f, setF] = useState({
    fuelType: formInitialData?.fuelType || "",
    vehicleNumber: formInitialData?.vehicleNumber || "",
    amount: formInitialData?.amount ?? "",
    quantity: formInitialData?.quantity ?? "",
    receivedBy: formInitialData?.receivedBy || "",
    approvedBy: formInitialData?.approvedBy || "",
    fuelDate: formInitialData?.fuelDate || "",
  });

  useEffect(() => {
    setF({
      fuelType: formInitialData?.fuelType || "",
      vehicleNumber: formInitialData?.vehicleNumber || "",
      amount: formInitialData?.amount ?? "",
      quantity: formInitialData?.quantity ?? "",
      receivedBy: formInitialData?.receivedBy || "",
      approvedBy: formInitialData?.approvedBy || "",
      fuelDate: formInitialData?.fuelDate || "",
    });
    setDocumentFile(null);
  }, [formInitialData?.id, formInitialData?.fuelType, formInitialData?.vehicleNumber, formInitialData?.amount, formInitialData?.quantity, formInitialData?.receivedBy, formInitialData?.approvedBy, formInitialData?.fuelDate]);

  useEffect(() => {
    let active = true;
    const loadEmployees = async ({ url, setEmployees, setError, setLoading, fieldLabel }) => {
      setLoading(true);
      setError("");
      try {
        const response = await fetchWithAuth(url, { method: "GET" });
        if (!response.ok) {
          throw new Error(`Employee request failed (${response.status})`);
        }
        const rows = getEmployeeRows(await response.json());
        if (!rows) {
          throw new Error("Employee response was not a list");
        }
        if (active) setEmployees(rows);
      } catch (error) {
        if (active) {
          console.error(`Error fetching employees for Pump Fuel ${fieldLabel}:`, error);
          setError(error.message || `Unable to load employees for ${fieldLabel}`);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadEmployees({
      url: API_ENDPOINTS.employee.list,
      setEmployees: setReceivedByEmployees,
      setError: setReceivedByError,
      setLoading: setReceivedByLoading,
      fieldLabel: "Received By",
    });
    loadEmployees({
      url: `${API_ENDPOINTS.employee.list}?isManagementGroup=true`,
      setEmployees: setApprovedByEmployees,
      setError: setApprovedByError,
      setLoading: setApprovedByLoading,
      fieldLabel: "Approved By",
    });

    return () => {
      active = false;
    };
  }, []);

  const receivedByValue = getSelectedEmployeeValue(f.receivedBy, receivedByEmployees);
  const approvedByValue = getSelectedEmployeeValue(f.approvedBy, approvedByEmployees);
  const receivedByOptions = receivedByValue && !receivedByEmployees.some(employee => employeeOptionValue(employee) === receivedByValue)
    ? [...receivedByEmployees, f.receivedBy]
    : receivedByEmployees;
  const approvedByOptions = approvedByValue && !approvedByEmployees.some(employee => employeeOptionValue(employee) === approvedByValue)
    ? [...approvedByEmployees, f.approvedBy]
    : approvedByEmployees;

  return (
    <div>
      <div className="form-grid">
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>Site</label>
          <input type="text" value={selectedSite?.name || selectedSite?.siteName || ""} readOnly style={{ backgroundColor: "#f1f5f9", cursor: "default" }} />
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-type">Fuel Type *</label>
          <select id="pump-fuel-type" value={f.fuelType} onChange={event => setF(current => ({ ...current, fuelType: event.target.value }))} required>
            <option value="">Select Fuel Type</option>
            <option value="DIESEL">Diesel</option>
            <option value="PETROL">Petrol</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-date">Fuel Date *</label>
          <input id="pump-fuel-date" type="date" value={f.fuelDate} onChange={event => setF(current => ({ ...current, fuelDate: event.target.value }))} required />
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-vehicle-number">Vehicle Number *</label>
          <input id="pump-fuel-vehicle-number" value={f.vehicleNumber} onChange={event => setF(current => ({ ...current, vehicleNumber: event.target.value }))} placeholder="e.g. UP32AB1234" required />
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-amount">Amount (₹) *</label>
          <input id="pump-fuel-amount" type="number" min="0" step="0.01" value={f.amount} onChange={event => setF(current => ({ ...current, amount: event.target.value }))} placeholder="0" required />
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-quantity">Quantity *</label>
          <input id="pump-fuel-quantity" type="number" min="0" step="0.01" value={f.quantity} onChange={event => setF(current => ({ ...current, quantity: event.target.value }))} placeholder="0" required />
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-received-by">Received By *</label>
          <select
            id="pump-fuel-received-by"
            value={receivedByValue}
            onChange={event => {
              const employee = receivedByEmployees.find(item => employeeOptionValue(item) === event.target.value) || null;
              setF(current => ({ ...current, receivedBy: employee }));
            }}
            disabled={receivedByLoading || Boolean(receivedByError)}
            required
          >
            <option value="">{receivedByLoading ? "Loading employees..." : receivedByError ? "Unable to load employees" : "Select employee"}</option>
            {receivedByOptions.map((employee, index) => {
              const value = typeof employee === "object" ? employeeOptionValue(employee) : String(employee);
              const label = typeof employee === "object" ? employeeLabel(employee) : String(employee);
              return <option key={value || `received-${index}`} value={value}>{label}</option>;
            })}
          </select>
          {receivedByError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{receivedByError}</p>}
        </div>
        <div className="field">
          <label htmlFor="pump-fuel-approved-by">Approved By *</label>
          <select
            id="pump-fuel-approved-by"
            value={approvedByValue}
            onChange={event => {
              const employee = approvedByEmployees.find(item => employeeOptionValue(item) === event.target.value) || null;
              setF(current => ({ ...current, approvedBy: employee }));
            }}
            disabled={approvedByLoading || Boolean(approvedByError)}
            required
          >
            <option value="">{approvedByLoading ? "Loading employees..." : approvedByError ? "Unable to load employees" : "Select employee"}</option>
            {approvedByOptions.map((employee, index) => {
              const value = typeof employee === "object" ? employeeOptionValue(employee) : String(employee);
              const label = typeof employee === "object" ? employeeLabel(employee) : String(employee);
              return <option key={value || `approved-${index}`} value={value}>{label}</option>;
            })}
          </select>
          {approvedByError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{approvedByError}</p>}
        </div>
        {!formInitialData && (
          <div className="field" style={{ gridColumn: "1 / -1" }}>
            <label htmlFor="pump-fuel-document">Document *</label>
            <input id="pump-fuel-document" type="file" required onChange={event => setDocumentFile(event.target.files?.[0] || null)} />
          </div>
        )}
      </div>
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={() => {
          const invalidFields = [];
          const amount = Number(f.amount);
          const quantity = Number(f.quantity);
          const receivedBy = getSelectedEmployee(f.receivedBy, receivedByEmployees);
          const approvedBy = getSelectedEmployee(f.approvedBy, approvedByEmployees);
          if (!f.fuelType) invalidFields.push("fuel type");
          if (!f.fuelDate) invalidFields.push("fuel date");
          if (!String(f.vehicleNumber).trim()) invalidFields.push("vehicle number");
          if (f.amount === "") invalidFields.push("a valid amount");
          else if (!Number.isFinite(amount) || amount < 0) invalidFields.push("a valid amount");
          if (f.quantity === "") invalidFields.push("a valid quantity");
          else if (!Number.isFinite(quantity) || quantity < 0) invalidFields.push("a valid quantity");
          if (!receivedBy) invalidFields.push("a valid Received By employee");
          if (!approvedBy) invalidFields.push("a valid Approved By employee");
          if (invalidFields.length > 0) {
            window.alert(`Please complete the required fields: ${invalidFields.join(", ")}.`);
            return;
          }
          if (!formInitialData && !documentFile) {
            window.alert("Please upload a document before saving Pump Fuel.");
            return;
          }
          onSave({
            id: formInitialData?.id ?? formInitialData?.fuelPumpId ?? null,
            fuelType: f.fuelType,
            vehicleNumber: String(f.vehicleNumber).trim(),
            amount: f.amount,
            quantity: f.quantity,
            receivedBy,
            approvedBy,
            fuelDate: f.fuelDate,
            site: selectedSite,
            documentFile,
          });
        }}>{formInitialData ? "Update Pump Fuel" : "Save Pump Fuel"}</button>
      </div>
    </div>
  );
}

export default FuelPumpForm;
